/**
 * Owns all audio resources for the call screen: microphone capture,
 * AI audio playback, device enumeration, and mid-session mic hot-swap.
 *
 * Audio flow:
 *   Input:  Mic (native rate) -> resample to negotiated PCM -> base64 -> WebSocket
 *   Output: Provider PCM at event rate -> resample to native rate -> scheduled playback,
 *           leaving the AudioContext via a per-platform route (see
 *           lib/utils/audio-output-route.ts) so phones classify the session
 *           as a voice call rather than media playback
 *   Recording: Mixed mic + AI output routed to a MediaStreamDestinationNode
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { captureException, captureEvent } from '../../../lib/telemetry/posthog/frontend';
import { getMediaStreamWithFallback } from '../../../lib/media-utils';
import { getAudioContextConstructor } from '../../../lib/utils/audio-context';
import {
  selectAudioOutputRoute,
  getAudioRouteEnvironment,
  detectMobileAudioPlatform,
  selectAndroidCallRoutePlan,
} from '../../../lib/utils/audio-output-route';
import { createAudioLoopback, type AudioLoopback } from '../audio-loopback';
import {
  appendPcm16Frames,
  encode,
  decode,
  decodeAudioData,
  resampleFloat32ToPcm16,
  StreamingPcm16Resampler,
} from '../audio-utils';
import type { RealtimeServerMessage } from '../protocol-types';
import type { ConversationTurn } from '../../../types';

interface UseAudioPipelineOptions {
  wsRef: React.MutableRefObject<WebSocket | null>;
  wsReadyRef: React.MutableRefObject<boolean>;
  preferredDeviceId?: string;
  preferredSpeakerId?: string;
}

/**
 * Grace after the AI's audio output goes idle before first-utterance protection
 * lifts. Bridges inter-chunk network jitter so a momentary gap between chunks
 * cannot lift protection mid-utterance.
 */
const PROTECTION_LIFT_TAIL_MS = 350;

export function useAudioPipeline({
  wsRef,
  wsReadyRef,
  preferredDeviceId,
  preferredSpeakerId,
}: UseAudioPipelineOptions) {
  const [isAiSpeaking, setIsAiSpeaking] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [analyser, setAnalyser] = useState<AnalyserNode | null>(null);
  const [inputAnalyser, setInputAnalyser] = useState<AnalyserNode | null>(null);
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>(preferredDeviceId || '');

  // Audio/mic refs -- exclusively owned by this hook
  const inputAudioContextRef = useRef<AudioContext | null>(null);
  const outputAudioContextRef = useRef<AudioContext | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const scriptProcessorRef = useRef<ScriptProcessorNode | null>(null);
  const mediaSourceNodeRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const nextStartTimeRef = useRef(0);
  /** Audio chunks still being decoded before their playback sources are scheduled. */
  const pendingAssistantAudioDecodesRef = useRef(0);
  /** Latches the terminal acknowledgement until every farewell audio source drains. */
  const endingConfirmationPendingRef = useRef(false);
  /** Provider-native microphone rate negotiated by realtime_session_ready. */
  const microphoneSampleRateHzRef = useRef(16_000);
  /** Preferred provider chunk duration used to packetize resampled PCM. */
  const preferredChunkDurationMsRef = useRef(40);
  const pendingMicrophonePcmRef = useRef<Int16Array<ArrayBufferLike>>(new Int16Array());
  const microphoneResamplerRef = useRef<StreamingPcm16Resampler | null>(null);
  const audioSourcesRef = useRef<Set<AudioBufferSourceNode>>(new Set());
  const playbackSourceMetaRef = useRef(new Map<AudioBufferSourceNode, {
    itemId: string;
    contentIndex: number;
    providerGeneration?: number;
    startTime: number;
    durationMs: number;
    /** Match worker PCM accounting despite per-chunk resampling frame rounding. */
    sourceDurationMs: number;
  }>());
  const completedPlaybackMsRef = useRef(new Map<string, number>());
  /** Natural completions cannot mix item ids reused after a provider reconnect. */
  const naturalCompletedPlaybackMsRef = useRef(new Map<string, number>());
  const audioOutputElementRef = useRef<HTMLAudioElement | null>(null);
  // Local RTCPeerConnection pair used on Android so playback qualifies as a
  // remote WebRTC track (the only path Android puts on the voice-call stream).
  const loopbackRef = useRef<AudioLoopback | null>(null);
  const mediaStreamDestRef = useRef<MediaStreamAudioDestinationNode | null>(null);
  const recordingAudioDestRef = useRef<MediaStreamAudioDestinationNode | null>(null);
  const micRecordingSourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const selectedDeviceIdRef = useRef<string>(preferredDeviceId || '');
  const swapTokenRef = useRef<number>(0);
  const handleTrackEndedRef = useRef<(() => void) | null>(null);
  // Analyser fed from the mic input - used by the bottom-bar level meter to indicate "we hear you".
  const inputAnalyserRef = useRef<AnalyserNode | null>(null);
  // Gate: don't stream the user's mic to the AI until it has begun its intro.
  // Users often say "hello" as soon as they join, which interrupts the intro.
  // Opens on the first AI audio chunk and stays open for the rest of the session.
  const aiIntroStartedRef = useRef<boolean>(false);
  // Synchronous mirror of isAiSpeaking. The audio callback runs outside React
  // and can't read state that lags a render behind, so it gates on this ref.
  const isAiSpeakingRef = useRef<boolean>(false);
  // First-utterance protection. While this is true AND the AI is speaking, the
  // mic is NOT forwarded upstream. Armed on first connect and re-armed on every
  // reconnect; lifted once the AI's first post-(re)connect utterance finishes.
  // This stops the AI from hearing its own resumed speech (which tripped
  // provider turn detection and made it interrupt itself mid-word on reconnect)
  // WITHOUT disabling barge-in for the rest of the conversation.
  const protectFirstUtteranceRef = useRef<boolean>(true);
  // Whether the AI has produced any audio since protection was last armed, so
  // the lift fires only after a real utterance (never on pre-speech silence,
  // which would leave the user able to talk but the AI still unprotected).
  const aiSpokeThisArmRef = useRef<boolean>(false);
  // Tail timer that lifts protection shortly after the AI's audio goes idle.
  const protectionLiftTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * Force-open the mic gate without waiting for an AI audio chunk.
   *
   * The gate normally opens when the AI's first audio frame arrives. On a
   * page refresh that lands inside the worker's grace period, the upstream
   * session stays alive and won't speak again, so no chunk arrives,
   * and the user is stuck unable to talk. The session hook calls this on
   * receiving a preserved-session reconnect with an immediately open mic gate.
   */
  const markAiIntroStarted = useCallback(() => {
    aiIntroStartedRef.current = true;
  }, []);

  /** Apply the active model's browser media policy without rebuilding the pipeline. */
  const setRealtimeAudioPolicy = useCallback((sampleRateHz: number, chunkDurationMs: number) => {
    let changed = false;
    if (Number.isFinite(sampleRateHz) && sampleRateHz > 0) {
      changed ||= microphoneSampleRateHzRef.current !== sampleRateHz;
      microphoneSampleRateHzRef.current = sampleRateHz;
    }
    if (Number.isFinite(chunkDurationMs) && chunkDurationMs > 0) {
      changed ||= preferredChunkDurationMsRef.current !== chunkDurationMs;
      preferredChunkDurationMsRef.current = chunkDurationMs;
    }
    if (changed) {
      pendingMicrophonePcmRef.current = new Int16Array();
      microphoneResamplerRef.current = null;
    }
  }, []);

  /**
   * Update AI-speaking state and drive first-utterance protection.
   *
   * Keeps {@link isAiSpeakingRef} in sync with the React state so the audio
   * callback can gate synchronously. When the AI starts speaking it cancels any
   * pending protection lift; when the AI goes idle after having spoken under an
   * active protection window, it schedules the lift after a short tail (so
   * inter-chunk jitter can't lift protection mid-utterance).
   */
  const setAiSpeaking = useCallback((speaking: boolean) => {
    isAiSpeakingRef.current = speaking;
    setIsAiSpeaking(speaking);
    if (speaking) {
      aiSpokeThisArmRef.current = true;
      if (protectionLiftTimerRef.current !== null) {
        clearTimeout(protectionLiftTimerRef.current);
        protectionLiftTimerRef.current = null;
      }
    } else if (protectFirstUtteranceRef.current && aiSpokeThisArmRef.current) {
      if (protectionLiftTimerRef.current !== null) clearTimeout(protectionLiftTimerRef.current);
      protectionLiftTimerRef.current = setTimeout(() => {
        protectFirstUtteranceRef.current = false;
        protectionLiftTimerRef.current = null;
      }, PROTECTION_LIFT_TAIL_MS);
    }
  }, []);

  /**
   * Re-arm first-utterance protection for a (re)connect: the AI's next utterance
   * will be shielded from the mic until it finishes. Called by the session hook
   * on every reconnect (and the initial ref value covers the first connect).
   */
  const armFirstUtteranceProtection = useCallback(() => {
    protectFirstUtteranceRef.current = true;
    aiSpokeThisArmRef.current = false;
    if (protectionLiftTimerRef.current !== null) {
      clearTimeout(protectionLiftTimerRef.current);
      protectionLiftTimerRef.current = null;
    }
  }, []);

  const updateSelectedDeviceId = useCallback((newId: string) => {
    selectedDeviceIdRef.current = newId;
    setSelectedDeviceId(newId);
  }, []);

  /** Send a JSON message to the live-session worker via WebSocket. */
  const sendToWorker = useCallback((data: unknown) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(data));
    }
  }, [wsRef]);

  /** Send the terminal acknowledgement only when no assistant audio can still play. */
  const maybeConfirmEndingAfterPlayback = useCallback(() => {
    if (
      !endingConfirmationPendingRef.current ||
      pendingAssistantAudioDecodesRef.current > 0 ||
      audioSourcesRef.current.size > 0
    ) {
      return;
    }

    endingConfirmationPendingRef.current = false;
    sendToWorker({ type: 'confirm_ending' });
  }, [sendToWorker]);

  /** Arm terminal close now, or immediately after the final scheduled AI audio ends. */
  const confirmEndingAfterPlayback = useCallback(() => {
    endingConfirmationPendingRef.current = true;
    maybeConfirmEndingAfterPlayback();
  }, [maybeConfirmEndingAfterPlayback]);

  /** Stop queued playback and report the cumulative heard position on interruption. */
  const stopAssistantPlayback = useCallback((interruptedItemId?: string) => {
    const contextTime = outputAudioContextRef.current?.currentTime ?? 0;
    const interruptedProgress = new Map<string, {
      itemId: string;
      contentIndex: number;
      audioEndMs: number;
    }>();

    if (interruptedItemId) {
      for (const [source, meta] of playbackSourceMetaRef.current) {
        if (meta.itemId !== interruptedItemId) continue;
        const key = `${meta.itemId}:${meta.contentIndex}`;
        const playedFromSource = Math.max(
          0,
          Math.min(meta.durationMs, (contextTime - meta.startTime) * 1_000),
        );
        const completed = completedPlaybackMsRef.current.get(key) ?? 0;
        const current = interruptedProgress.get(key);
        interruptedProgress.set(key, {
          itemId: meta.itemId,
          contentIndex: meta.contentIndex,
          audioEndMs: (current?.audioEndMs ?? completed) + playedFromSource,
        });
        playbackSourceMetaRef.current.delete(source);
      }
      for (const [key, completed] of completedPlaybackMsRef.current) {
        if (!key.startsWith(`${interruptedItemId}:`) || interruptedProgress.has(key)) continue;
        const contentIndex = Number(key.slice(key.lastIndexOf(':') + 1));
        interruptedProgress.set(key, {
          itemId: interruptedItemId,
          contentIndex,
          audioEndMs: completed,
        });
      }
    }

    playbackSourceMetaRef.current.clear();
    completedPlaybackMsRef.current.clear();
    naturalCompletedPlaybackMsRef.current.clear();
    audioSourcesRef.current.forEach((source) => {
      try { source.stop(); } catch { /* ignore */ }
    });
    audioSourcesRef.current.clear();
    nextStartTimeRef.current = 0;
    setAiSpeaking(false);

    for (const progress of interruptedProgress.values()) {
      sendToWorker({
        type: 'assistant_playback_progress',
        ...progress,
        audioEndMs: Math.round(progress.audioEndMs),
      });
    }
  }, [sendToWorker, setAiSpeaking]);

  /**
   * Setup the full audio pipeline during session start.
   * Returns the analyserNode for use in the message handler.
   */
  const setupAudioPipeline = useCallback(async (): Promise<{
    analyserNode: AnalyserNode;
    stream: MediaStream;
  }> => {
    const deviceToUse = selectedDeviceIdRef.current || undefined;

    // Choose how playback will leave the AudioContext. On phones the OS must
    // classify this session as a voice call (loudspeaker, volume rocker on
    // call volume, audible while media volume is muted, AEC-aware playback);
    // lib/utils/audio-output-route.ts documents the per-platform reasoning.
    const routeEnv = getAudioRouteEnvironment(preferredSpeakerId);
    const platform = detectMobileAudioPlatform(routeEnv.userAgent, routeEnv.maxTouchPoints);
    const route = selectAudioOutputRoute(routeEnv);

    // iOS 16.4+ Audio Session API: declare two-way call intent before the mic
    // is acquired so WebKit starts on the play-and-record session instead of
    // transitioning into it. iOS only: the API does not exist on Android, and
    // desktop Safari must keep its default session.
    if (platform === 'ios') {
      const nav = navigator as Navigator & { audioSession?: { type: string } };
      if (nav.audioSession) nav.audioSession.type = 'play-and-record';
    }

    const result = await getMediaStreamWithFallback({
      deviceId: deviceToUse,
      kind: 'audio',
      constraints: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
      context: 'session_start',
    });
    let stream = result.stream;
    mediaStreamRef.current = stream;

    // Telemetry only: the AEC/noise-suppression/AGC constraints are requested as
    // best-effort booleans, so a device/OS that can't honor one silently returns
    // a track without it. Surface that so self-hearing reports can be correlated
    // with a browser that quietly dropped echo cancellation.
    const appliedSettings = stream.getAudioTracks()[0]?.getSettings?.();
    if (
      appliedSettings &&
      (appliedSettings.echoCancellation === false ||
        appliedSettings.noiseSuppression === false ||
        appliedSettings.autoGainControl === false)
    ) {
      console.warn('[AudioPipeline] Audio constraints not fully applied by the browser', {
        echoCancellation: appliedSettings.echoCancellation,
        noiseSuppression: appliedSettings.noiseSuppression,
        autoGainControl: appliedSettings.autoGainControl,
      });
    }

    // Enumerate devices now that mic permission grants label visibility
    const allDevices = await navigator.mediaDevices.enumerateDevices();
    const audioInputs = allDevices.filter(d => d.kind === 'audioinput');
    setDevices(audioInputs);

    if (result.actualDeviceId) {
      updateSelectedDeviceId(result.actualDeviceId);
    }

    // Separate contexts: input captures at the device-native rate and resamples
    // once to the selected profile's wire rate. Output uses each event's rate.
    inputAudioContextRef.current = new (getAudioContextConstructor())();
    const outputCtx = new (getAudioContextConstructor())();
    outputAudioContextRef.current = outputCtx;

    const analyserNode = outputCtx.createAnalyser();
    analyserNode.fftSize = 256;
    // Lower than the 0.8 default so hollie's orb (the only consumer of this
    // analyser) sees the voice envelope's natural rise and fall. At 0.8 the byte
    // data is so temporally smoothed the orb reads a sustained level and just
    // stays swelled while she talks instead of pulsing with her speech.
    analyserNode.smoothingTimeConstant = 0.6;

    if (route === 'direct') {
      analyserNode.connect(outputCtx.destination);
    } else {
      const dest = outputCtx.createMediaStreamDestination();
      mediaStreamDestRef.current = dest;
      analyserNode.connect(dest);

      const audioEl = new Audio();
      audioEl.autoplay = true;
      // WebKit needs playsinline for stream playback to stay in-page.
      audioEl.setAttribute('playsinline', '');

      let playbackStream: MediaStream = dest.stream;
      if (route === 'webrtc-loopback') {
        try {
          const loopback = await createAudioLoopback(dest.stream);
          loopbackRef.current = loopback;
          playbackStream = loopback.remoteStream;
        } catch (error) {
          // Degraded fallback: play the Web Audio stream directly. Still
          // audible, but on the media stream (media volume, no call routing).
          captureException(
            error instanceof Error ? error : String(error),
            'useAudioPipeline.setupAudioPipeline',
            { stage: 'webrtc_loopback' },
          );
        }
      }

      audioEl.srcObject = playbackStream;

      // Desktop speaker selection. Never applied to the loopback route:
      // moving WebRTC playout off the default device can drop the echo
      // canceller's reference signal (Chromium issue 40252911), and phones
      // do not expose setSinkId anyway.
      if (
        route === 'media-element' &&
        preferredSpeakerId &&
        'setSinkId' in HTMLAudioElement.prototype
      ) {
        try {
          await (audioEl as HTMLAudioElement & {
            setSinkId?(id: string): Promise<void>;
          }).setSinkId?.(preferredSpeakerId);
        } catch (e) {
          console.warn('Could not set speaker device:', e);
        }
      }

      audioOutputElementRef.current = audioEl;
      // Autoplay is expected to be allowed while a capture session is live,
      // but kick playback explicitly and surface refusals: a blocked element
      // here means the respondent hears nothing at all.
      void audioEl.play().catch((error) => {
        captureException(
          error instanceof Error ? error : String(error),
          'useAudioPipeline.setupAudioPipeline',
          { stage: 'audio_element_play' },
        );
      });
    }

    // Android call-route latch. Chromium enters MODE_IN_COMMUNICATION when
    // the FIRST mic capture opens, but issues its communication-device
    // selection just before that mode flips (ordering race in
    // media/audio/android/audio_manager_android.cc), so the route requested
    // at session start never latches: playout lands on the media volume
    // stream or the earpiece. The reliable page-level trigger is a GENUINE
    // device change while a capture stays open and playout is live, so with
    // the loopback element now playing, bounce the mic through another
    // built-in device and land on the speakerphone. Each capture is acquired
    // BEFORE the previous one stops: a zero-capture moment makes Chromium
    // reset communication mode and lose the latch.
    if (route === 'webrtc-loopback' && platform === 'android') {
      const plan = selectAndroidCallRoutePlan(audioInputs);
      if (plan) {
        const acquireMic = (deviceId: string, context: string) =>
          getMediaStreamWithFallback({
            deviceId,
            kind: 'audio',
            constraints: {
              echoCancellation: true,
              noiseSuppression: true,
              autoGainControl: true,
            },
            context,
          });
        /** Swap the pipeline onto a new capture, stopping the old one after. */
        const adoptStream = (next: MediaStream, actualDeviceId?: string) => {
          const previous = stream;
          stream = next;
          mediaStreamRef.current = next;
          previous.getTracks().forEach(track => track.stop());
          if (actualDeviceId) {
            updateSelectedDeviceId(actualDeviceId);
          }
        };

        let bounceOutcome = plan.bounceId ? 'ok' : 'unavailable';
        if (plan.bounceId) {
          try {
            const bounced = await acquireMic(plan.bounceId, 'android_route_bounce');
            adoptStream(bounced.stream, bounced.actualDeviceId);
          } catch (error) {
            bounceOutcome = 'failed';
            captureException(
              error instanceof Error ? error : String(error),
              'useAudioPipeline.setupAudioPipeline',
              { stage: 'android_route_bounce' },
            );
          }
        }

        let speakerphoneOutcome = 'ok';
        try {
          const speaker = await acquireMic(plan.speakerphoneId, 'android_speakerphone_route');
          adoptStream(speaker.stream, speaker.actualDeviceId);
        } catch (error) {
          // Degraded: stay on whatever mic is held. If the bounce succeeded
          // the call is at least on the communication route (earpiece).
          speakerphoneOutcome = 'failed';
          captureException(
            error instanceof Error ? error : String(error),
            'useAudioPipeline.setupAudioPipeline',
            { stage: 'android_speakerphone_route' },
          );
        }

        // Field diagnostics for OEM routing variance: which devices the plan
        // picked and whether each latch step held.
        captureEvent('android_call_route_latch', {
          bounceDeviceId: plan.bounceId ?? 'none',
          speakerphoneDeviceId: plan.speakerphoneId,
          bounce: bounceOutcome,
          speakerphone: speakerphoneOutcome,
        });
      }
    }

    setAnalyser(analyserNode);

    // Recording destination mixes mic + AI playback into one stream for video recording
    const recordingDest = outputCtx.createMediaStreamDestination();
    recordingAudioDestRef.current = recordingDest;
    micRecordingSourceRef.current = outputCtx.createMediaStreamSource(mediaStreamRef.current);
    micRecordingSourceRef.current.connect(recordingDest);

    const source = inputAudioContextRef.current.createMediaStreamSource(mediaStreamRef.current);
    mediaSourceNodeRef.current = source;

    // Mic-level analyser (independent of pause/mute so the meter keeps updating).
    // Tap the source *before* the scriptProcessor so disconnect-on-mute only affects the WS send path.
    const inputAnalyserNode = inputAudioContextRef.current.createAnalyser();
    inputAnalyserNode.fftSize = 32;
    inputAnalyserNode.smoothingTimeConstant = 0.5;
    source.connect(inputAnalyserNode);
    inputAnalyserRef.current = inputAnalyserNode;
    setInputAnalyser(inputAnalyserNode);

    const scriptProcessor = inputAudioContextRef.current.createScriptProcessor(2048, 1, 1);
    scriptProcessorRef.current = scriptProcessor;

    const inputSampleRate = inputAudioContextRef.current.sampleRate;

    scriptProcessor.onaudioprocess = (audioProcessingEvent) => {
      if (!wsReadyRef.current) return;
      if (!aiIntroStartedRef.current) return;
      // Barge-in stays enabled in steady state, but NOT during the AI's first
      // utterance after a (re)connect: mute the mic while that utterance plays
      // so the AI can't hear itself before the echo-canceller has reconverged.
      if (protectFirstUtteranceRef.current && isAiSpeakingRef.current) return;

      const inputData = audioProcessingEvent.inputBuffer.getChannelData(0);
      const targetSampleRate = microphoneSampleRateHzRef.current;
      const currentResampler = microphoneResamplerRef.current;
      if (
        !currentResampler
        || currentResampler.sourceSampleRate !== inputSampleRate
        || currentResampler.targetSampleRate !== targetSampleRate
      ) {
        microphoneResamplerRef.current = new StreamingPcm16Resampler(
          inputSampleRate,
          targetSampleRate,
        );
      }
      const resampler = microphoneResamplerRef.current;
      if (!resampler) return;
      const int16 = resampleFloat32ToPcm16(
        inputData,
        inputSampleRate,
        targetSampleRate,
        resampler,
      );
      const samplesPerChunk = Math.max(1, Math.round(
        microphoneSampleRateHzRef.current * preferredChunkDurationMsRef.current / 1_000,
      ));
      const packetized = appendPcm16Frames(
        pendingMicrophonePcmRef.current,
        int16,
        samplesPerChunk,
      );
      pendingMicrophonePcmRef.current = packetized.remainder;
      for (const chunk of packetized.chunks) {
        sendToWorker({
          type: 'audio',
          data: encode(new Uint8Array(chunk.buffer, chunk.byteOffset, chunk.byteLength)),
        });
      }
    };
    source.connect(scriptProcessor);
    // ScriptProcessorNode requires a connected output to fire onaudioprocess.
    // Route through a zero-gain node so no mic audio leaks to speakers.
    const silentGain = inputAudioContextRef.current.createGain();
    silentGain.gain.value = 0;
    scriptProcessor.connect(silentGain);
    silentGain.connect(inputAudioContextRef.current.destination);

    // Attach track.onended for auto-recovery
    const audioTrack = stream.getAudioTracks()[0];
    if (audioTrack) {
      audioTrack.onended = () => handleTrackEndedRef.current?.();
    }

    return { analyserNode, stream };
  }, [wsReadyRef, preferredSpeakerId, updateSelectedDeviceId, sendToWorker]);

  /**
   * Process one holito semantic realtime event and schedule audio gap-free.
   */
  const playAudioChunk = useCallback(async (
    message: RealtimeServerMessage,
    analyserNode: AnalyserNode | null,
    currentStatus: string,
    setStatus: (s: string | ((prev: string) => string)) => void,
    setConversation: React.Dispatch<React.SetStateAction<ConversationTurn[]>>,
    setQuestionsComplete: (v: boolean) => void,
    onPerformResearch: (query: string, type: 'primary' | 'background') => void,
  ) => {
    if (message.type === 'realtime_interrupted') {
      stopAssistantPlayback(message.itemId);
      return;
    }

    // Authoritative end-of-turn: lift first-utterance protection the moment the
    // AI's turn completes, rather than relying solely on the audio-drain tail
    // (which a long inter-chunk network gap could trip early, mid-utterance).
    // The tail timer in setAiSpeaking remains a fallback if the semantic
    // response-completed event is never delivered.
    if (
      message.type === 'realtime_response_state' && message.state === 'completed' &&
      protectFirstUtteranceRef.current &&
      aiSpokeThisArmRef.current
    ) {
      if (protectionLiftTimerRef.current !== null) {
        clearTimeout(protectionLiftTimerRef.current);
        protectionLiftTimerRef.current = null;
      }
      protectFirstUtteranceRef.current = false;
    }

    if (message.type === 'realtime_transcript' && message.speaker === 'user') {
      const text = message.text;
      if (!text) return;
      setConversation((prev) => {
        const last = prev[prev.length - 1];
        if (last?.speaker === 'user') {
          return [...prev.slice(0, -1), { ...last, text: last.text + text }];
        }
        return [...prev, { speaker: 'user', text }];
      });
    }

    if (message.type === 'realtime_transcript' && message.speaker === 'ai') {
      const text = message.text;
      if (!text) return;
      setConversation((prev) => {
        const last = prev[prev.length - 1];
        if (last?.speaker === 'ai') {
          return [...prev.slice(0, -1), { ...last, text: last.text + text }];
        }
        return [...prev, { speaker: 'ai', text }];
      });
    }

    if (message.type === 'realtime_tool_state' && message.state === 'called') {
      if (message.name === 'end_conversation') {
        setQuestionsComplete(true);
      }
      if (message.name === 'perform_research' && typeof message.arguments?.query === 'string') {
        onPerformResearch(message.arguments.query, 'primary');
      }
    }

    if (message.type === 'realtime_audio' && message.data && outputAudioContextRef.current) {
      aiIntroStartedRef.current = true;
      setAiSpeaking(true);
      if (currentStatus === 'Nudging AI...') setStatus('Listening...');

      const ctx = outputAudioContextRef.current;
      const nextTime = Math.max(nextStartTimeRef.current, ctx.currentTime);
      pendingAssistantAudioDecodesRef.current += 1;
      try {
        const sourcePcm = decode(message.data);
        const audioBuffer = await decodeAudioData(sourcePcm, ctx, message.sampleRateHz, 1);
        const sourceNode = ctx.createBufferSource();
        sourceNode.buffer = audioBuffer;
        const playbackTarget =
          analyserNode && analyserNode.context === ctx ? analyserNode : ctx.destination;
        if (analyserNode && analyserNode.context !== ctx) {
          console.warn('[AudioPipeline] Ignoring stale analyser from different AudioContext');
        }
        sourceNode.connect(playbackTarget);

        // Guard against stale nodes from a previous AudioContext (can happen during dev reconnects).
        if (recordingAudioDestRef.current && recordingAudioDestRef.current.context === ctx) {
          sourceNode.connect(recordingAudioDestRef.current);
        } else if (recordingAudioDestRef.current) {
          console.warn('[AudioPipeline] Ignoring stale recording destination from different AudioContext');
        }
        sourceNode.start(nextTime);
        nextStartTimeRef.current = nextTime + audioBuffer.duration;
        audioSourcesRef.current.add(sourceNode);
        playbackSourceMetaRef.current.set(sourceNode, {
          itemId: message.itemId,
          contentIndex: message.contentIndex,
          providerGeneration: message.providerGeneration,
          startTime: nextTime,
          durationMs: audioBuffer.duration * 1_000,
          sourceDurationMs: sourcePcm.byteLength / 2 / message.sampleRateHz * 1_000,
        });
        sourceNode.onended = () => {
          audioSourcesRef.current.delete(sourceNode);
          const meta = playbackSourceMetaRef.current.get(sourceNode);
          if (meta) {
            const key = `${meta.itemId}:${meta.contentIndex}`;
            const completedMs = (completedPlaybackMsRef.current.get(key) ?? 0) + meta.durationMs;
            completedPlaybackMsRef.current.set(key, completedMs);
            playbackSourceMetaRef.current.delete(sourceNode);
            if (typeof meta.providerGeneration === 'number') {
              const naturalKey = `${meta.providerGeneration}:${key}`;
              const naturalCompletedMs = (naturalCompletedPlaybackMsRef.current.get(naturalKey) ?? 0) + meta.sourceDurationMs;
              naturalCompletedPlaybackMsRef.current.set(naturalKey, naturalCompletedMs);
              sendToWorker({ type: 'assistant_playback_completed', itemId: meta.itemId,
                contentIndex: meta.contentIndex, providerGeneration: meta.providerGeneration,
                audioEndMs: Math.round(naturalCompletedMs) });
            }
          }
          if (audioSourcesRef.current.size === 0) setAiSpeaking(false);
          maybeConfirmEndingAfterPlayback();
        };
      } finally {
        pendingAssistantAudioDecodesRef.current -= 1;
        maybeConfirmEndingAfterPlayback();
      }
    }
  }, [maybeConfirmEndingAfterPlayback, sendToWorker, setAiSpeaking, stopAssistantPlayback]);

  /** Stop all playing audio sources immediately (e.g., user started speaking). */
  const handleInterruption = useCallback(() => {
    stopAssistantPlayback();
  }, [stopAssistantPlayback]);

  /**
   * Hot-swap the microphone mid-session without dropping the WebSocket connection.
   * Uses a swap token to discard stale results if multiple swaps overlap.
   */
  const swapMic = useCallback(async (newDeviceId?: string) => {
    const token = ++swapTokenRef.current;

    if (!mediaStreamRef.current || !inputAudioContextRef.current || !scriptProcessorRef.current) return;

    try {
      mediaStreamRef.current.getAudioTracks().forEach(t => { t.onended = null; });

      const result = await getMediaStreamWithFallback({
        deviceId: newDeviceId,
        kind: 'audio',
        constraints: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
        context: 'mid_session_swap',
      });

      if (token !== swapTokenRef.current) {
        result.stream.getTracks().forEach(t => t.stop());
        return;
      }

      mediaStreamRef.current.getTracks().forEach(track => track.stop());
      mediaStreamRef.current = result.stream;

      if (mediaSourceNodeRef.current) {
        try { mediaSourceNodeRef.current.disconnect(); } catch (_) { /* already disconnected */ }
        mediaSourceNodeRef.current = null;
      }
      const newSource = inputAudioContextRef.current.createMediaStreamSource(result.stream);
      mediaSourceNodeRef.current = newSource;

      // Re-wire the mic-level analyser so the bottom-bar meter keeps tracking the new mic.
      if (inputAnalyserRef.current) {
        newSource.connect(inputAnalyserRef.current);
      }

      if (!isPaused && scriptProcessorRef.current) {
        newSource.connect(scriptProcessorRef.current);
      }

      if (micRecordingSourceRef.current) {
        try { micRecordingSourceRef.current.disconnect(); } catch (_) { /* already disconnected */ }
        micRecordingSourceRef.current = null;
      }
      if (outputAudioContextRef.current && recordingAudioDestRef.current) {
        micRecordingSourceRef.current = outputAudioContextRef.current.createMediaStreamSource(result.stream);
        micRecordingSourceRef.current.connect(recordingAudioDestRef.current);
      }

      const newAudioTrack = result.stream.getAudioTracks()[0];
      if (newAudioTrack) {
        newAudioTrack.onended = () => handleTrackEndedRef.current?.();
      }

      if (result.actualDeviceId) {
        updateSelectedDeviceId(result.actualDeviceId);
      }

      if (result.fallbackUsed) {
        console.warn(
          `[AudioPipeline] Mic fallback: requested=${result.requestedDeviceId}, actual=${result.actualDeviceId}`
        );
      }

      const allDevices = await navigator.mediaDevices.enumerateDevices();
      if (token !== swapTokenRef.current) return;
      const audioInputs = allDevices.filter(d => d.kind === 'audioinput');
      setDevices(audioInputs);

    } catch (error) {
      console.error('[AudioPipeline] Failed to hot-swap microphone:', error);
      captureException(error instanceof Error ? error : String(error), 'useAudioPipeline.hotSwapMicrophone', { newDeviceId });
    }
  }, [isPaused, updateSelectedDeviceId]);

  /** Auto-recovery when the active mic track ends (e.g., Bluetooth device disconnected). */
  const handleTrackEnded = useCallback((
    hasEndedRef: React.MutableRefObject<boolean>,
    connectedRef: React.MutableRefObject<boolean>,
    setStatus: (s: string | ((prev: string) => string)) => void,
  ) => {
    if (hasEndedRef.current || !connectedRef.current) return;

    console.warn('[AudioPipeline] Active mic track ended, attempting auto-recovery');

    setStatus('Mic disconnected - switching to default...');

    void swapMic(undefined).then(() => {
      setTimeout(() => {
        setStatus((prev: string) => prev.includes('disconnected') || prev.includes('switching') ? 'Listening...' : prev);
      }, 3000);
    });
  }, [swapMic]);

  // handleTrackEndedRef is populated by useSessionWebSocket which injects
  // session-scoped refs (hasEndedRef, connectedRef). No default is needed here.

  /** Toggle mic capture on/off by connecting/disconnecting the source node. */
  const togglePause = useCallback(() => {
    if (!scriptProcessorRef.current || !mediaSourceNodeRef.current || !inputAudioContextRef.current) return;
    if (isPaused) {
      mediaSourceNodeRef.current.connect(scriptProcessorRef.current);
    } else {
      mediaSourceNodeRef.current.disconnect(scriptProcessorRef.current);
    }
    setIsPaused(!isPaused);
  }, [isPaused]);

  /** Handle user selecting a different microphone from the dropdown. */
  const handleDeviceChange = useCallback(async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newDeviceId = e.target.value;
    updateSelectedDeviceId(newDeviceId);
    await swapMic(newDeviceId);
  }, [updateSelectedDeviceId, swapMic]);

  /** Release all audio resources: stop tracks, disconnect nodes, close contexts. */
  const cleanupAudio = useCallback(() => {
    swapTokenRef.current++;

    if (protectionLiftTimerRef.current !== null) {
      clearTimeout(protectionLiftTimerRef.current);
      protectionLiftTimerRef.current = null;
    }

    if (mediaStreamRef.current) {
      mediaStreamRef.current.getAudioTracks().forEach(t => { t.onended = null; });
      mediaStreamRef.current.getTracks().forEach(track => track.stop());
    }
    if (scriptProcessorRef.current) { scriptProcessorRef.current.disconnect(); }

    if (micRecordingSourceRef.current) {
      try { micRecordingSourceRef.current.disconnect(); } catch (_) { /* already disconnected */ }
      micRecordingSourceRef.current = null;
    }

    if (loopbackRef.current) {
      loopbackRef.current.close();
      loopbackRef.current = null;
    }

    if (audioOutputElementRef.current) {
      audioOutputElementRef.current.pause();
      audioOutputElementRef.current.srcObject = null;
      audioOutputElementRef.current = null;
    }

    recordingAudioDestRef.current = null;
    playbackSourceMetaRef.current.clear();
    completedPlaybackMsRef.current.clear();
    pendingAssistantAudioDecodesRef.current = 0;
    naturalCompletedPlaybackMsRef.current.clear();
    endingConfirmationPendingRef.current = false;
    pendingMicrophonePcmRef.current = new Int16Array();
    microphoneResamplerRef.current = null;
    audioSourcesRef.current.clear();

    if (inputAudioContextRef.current && inputAudioContextRef.current.state !== 'closed') {
      void inputAudioContextRef.current.close();
    }
    if (outputAudioContextRef.current && outputAudioContextRef.current.state !== 'closed') {
      void outputAudioContextRef.current.close();
    }
  }, []);

  // Keep the device dropdown in sync when devices are plugged/unplugged
  useEffect(() => {
    const handleDevicesChanged = async () => {
      try {
        const allDevices = await navigator.mediaDevices.enumerateDevices();
        const audioInputs = allDevices.filter(d => d.kind === 'audioinput');
        setDevices(audioInputs);
      } catch (_) {
        // enumerateDevices can fail if permissions were revoked
      }
    };

    navigator.mediaDevices.addEventListener('devicechange', handleDevicesChanged);
    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', handleDevicesChanged);
    };
  }, []);

  return {
    // Read-only state
    isAiSpeaking,
    isPaused,
    analyser,
    inputAnalyser,
    devices,
    selectedDeviceId,
    // Refs needed by session hook
    recordingAudioDestRef,
    mediaStreamRef,
    // High-level commands
    setupAudioPipeline,
    playAudioChunk,
    handleInterruption,
    swapMic,
    handleTrackEnded,
    handleTrackEndedRef,
    togglePause,
    handleDeviceChange,
    cleanupAudio,
    sendToWorker,
    confirmEndingAfterPlayback,
    // State setters needed by session hook handlers.
    // Expose the ref-syncing setAiSpeaking (not the raw React setter) so any
    // external caller keeps isAiSpeakingRef - which the audio callback reads -
    // in sync with the rendered state.
    setIsAiSpeaking: setAiSpeaking,
    setIsPaused,
    markAiIntroStarted,
    armFirstUtteranceProtection,
    setRealtimeAudioPolicy,
  };
}
